// SPDX-License-Identifier: GPL-3.0-or-later
//
// Detects whether the Applify browser extension is installed. The extension's
// content script (extension/bridge.js) runs at document_start on this origin,
// sets a `data-applify-extension` marker on <html>, and dispatches
// `applify:extension-present`. We read the marker synchronously (the common
// case, since the content script runs first) and also listen for the event and
// fire one ping (covers the race where the page booted first).

const ATTR = 'data-applify-extension';
const PRESENT_EVENT = 'applify:extension-present';
const PING_EVENT = 'applify:detect-ping';

export interface ExtensionState {
  installed: boolean;
  version: string | null;
}

function readMarker(): ExtensionState {
  const version = document.documentElement.getAttribute(ATTR);
  return { installed: version !== null, version: version || null };
}

let state: ExtensionState = readMarker();

type Listener = (s: ExtensionState) => void;
const listeners = new Set<Listener>();

function set(next: ExtensionState): void {
  if (next.installed === state.installed && next.version === state.version) return;
  state = next;
  for (const fn of listeners) fn(state);
}

export function getExtensionState(): ExtensionState {
  return state;
}

export function subscribeExtension(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

window.addEventListener(PRESENT_EVENT, (e: Event) => {
  const detail = (e as CustomEvent).detail as { version?: string } | undefined;
  set({ installed: true, version: detail?.version ?? state.version ?? null });
});

// Ask any already-present content script to re-announce.
window.dispatchEvent(new CustomEvent(PING_EVENT));

// Re-probe when the tab regains focus (e.g. after installing the extension in
// another tab and switching back). Also re-reads the marker in case a freshly
// injected content script set it without us catching the event.
function reprobe(): void {
  if (!state.installed) {
    const marker = readMarker();
    if (marker.installed) set(marker);
    window.dispatchEvent(new CustomEvent(PING_EVENT));
  }
}
window.addEventListener('focus', reprobe);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') reprobe();
});
